import { useState, useEffect, useRef, useCallback } from "react";

// Default API base URL
const DEFAULT_BASE_URL = "https://api.zovalife.co.uk/api/";

/**
 * Custom hook for making API requests with automatic retry functionality
 * @param {string} endpoint - The API endpoint to fetch data from (will be appended to base URL)
 * @param {Object} options - Additional options for the fetch request
 * @param {Object} config - Configuration options for the hook
 * @param {string} config.baseUrl - Base URL for the API (defaults to "https://api.zovalife.co.uk/api/")
 * @param {number} config.maxRetries - Maximum number of retry attempts (defaults to 12)
 * @param {number} config.retryDelay - Delay between retries in milliseconds (defaults to 5000)
 * @param {Function} onSuccess - Callback function to run on successful request
 * @param {Function} onError - Callback function to run when all retries fail
 * @returns {Object} - Object containing request state and control functions
 */
const useApiWithRetry = (
    endpoint,
    options = {},
    config = {},
    onSuccess = () => {},
    onError = () => {}
) => {
    // Extract configuration with defaults
    const { 
        baseUrl = DEFAULT_BASE_URL,
        maxRetries = 12,
        retryDelay = 5000
    } = config;
    
    // Store config values in refs to prevent dependency changes
    const configRef = useRef({ baseUrl, maxRetries, retryDelay });
    const onSuccessRef = useRef(onSuccess);
    const onErrorRef = useRef(onError);
    const optionsRef = useRef(options);
    const endpointRef = useRef(endpoint);
    
    // Flag to track if we've already made the initial request
    const hasInitialRequestBeenMade = useRef(false);
    
    // Update refs when props change
    useEffect(() => {
        configRef.current = { baseUrl, maxRetries, retryDelay };
        onSuccessRef.current = onSuccess;
        onErrorRef.current = onError;
        optionsRef.current = options;
        endpointRef.current = endpoint;
    }, [baseUrl, maxRetries, retryDelay, onSuccess, onError, options, endpoint]);
    
    // Construct the full URL - this is now a derived value from the ref
    const urlRef = useRef(`${baseUrl}${endpoint}`);
    
    useEffect(() => {
        urlRef.current = `${configRef.current.baseUrl}${endpointRef.current}`;
    }, [endpoint, baseUrl]);
    
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [retryCount, setRetryCount] = useState(0);
    
    const isRetryingRef = useRef(false);
    const requestInProgressRef = useRef(false);
    const retryTimeoutRef = useRef(null);
    
    // Function to handle retry logic
    const handleRetry = useCallback((errorMessage, statusCode) => {
        const { maxRetries, retryDelay } = configRef.current;
        
        if (retryCount < maxRetries) {
            const retryMessage = statusCode 
                ? `${errorMessage}, retrying in ${retryDelay/1000} seconds... (${retryCount + 1}/${maxRetries})`
                : `${errorMessage}, retrying in ${retryDelay/1000} seconds... (${retryCount + 1}/${maxRetries})`;
            
            console.log(retryMessage);
            
            // Clear any existing timeout to prevent duplicate retries
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
            }
            
            // Schedule the next retry after the delay
            retryTimeoutRef.current = setTimeout(() => {
                isRetryingRef.current = false;
                requestInProgressRef.current = false;
                setRetryCount(prevCount => prevCount + 1);
            }, retryDelay);
        } else {
            const failureMessage = statusCode
                ? `Failed after ${maxRetries} retries. Last status: ${statusCode}`
                : `Failed after ${maxRetries} retries due to ${errorMessage}`;
            
            console.error(failureMessage);
            setLoading(false);
            setError(failureMessage);
            isRetryingRef.current = false;
            requestInProgressRef.current = false;
            
            // Call the onError callback
            onErrorRef.current(failureMessage);
        }
    }, [retryCount]);
    
    // Function to fetch data
    const fetchData = useCallback(async () => {
        // If we've already made the initial request, don't make another one
        if (hasInitialRequestBeenMade.current && retryCount === 0) {
            console.log("Initial request already made, skipping duplicate call");
            return;
        }
        
        // Prevent multiple retries from running simultaneously
        if (isRetryingRef.current || requestInProgressRef.current) {
            return;
        }
        
        try {
            isRetryingRef.current = true;
            requestInProgressRef.current = true;
            
            // Mark that we've made the initial request
            if (retryCount === 0) {
                hasInitialRequestBeenMade.current = true;
            }
            
            console.log(`Making request attempt ${retryCount + 1} to ${urlRef.current}`);
            const response = await fetch(urlRef.current, optionsRef.current);
            
            if (response.ok) {
                const responseData = await response.json();
                setData(responseData);
                setLoading(false);
                setError(null);
                isRetryingRef.current = false;
                requestInProgressRef.current = false;
                
                // Call the onSuccess callback
                onSuccessRef.current(responseData);
            } else {
                // Handle all non-200 responses with a consistent retry mechanism
                let errorMessage = "";
                
                if (response.status === 404) {
                    errorMessage = "Resource not found";
                } else if (response.status === 429) {
                    errorMessage = "Rate limited";
                } else {
                    errorMessage = `Error (${response.status})`;
                }
                
                handleRetry(errorMessage, response.status);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            
            // Handle network errors with the same retry mechanism
            handleRetry("Network error");
        }
    }, [retryCount, handleRetry]);
    
    // Reset the retry count and trigger a new request
    const resetAndRetry = useCallback(() => {
        setRetryCount(0);
        setError(null);
        setLoading(true);
        
        // Clear any existing timeout
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
        }
        
        isRetryingRef.current = false;
        requestInProgressRef.current = false;
        hasInitialRequestBeenMade.current = false; // Reset the initial request flag
    }, []);
    
    // Effect to fetch data when the retryCount changes
    useEffect(() => {
        fetchData();
        
        // Cleanup function to prevent memory leaks
        return () => {
            isRetryingRef.current = false;
            requestInProgressRef.current = false;
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
            }
        };
    }, [fetchData]);
    
    return {
        data,
        loading,
        error,
        retryCount,
        resetAndRetry
    };
};

export default useApiWithRetry; 